import { useCallback, useRef } from 'react';
import Actions from '../store/Actions';

interface UseSSEConnectionOptions<T> {
  onMessage: (data: T) => void;
  onError?: (error: Error) => void;
  onClose?: () => void;
}

/**
 * Generic hook for managing Server-Sent Events (SSE) connections.
 * Centralizes SSE connection logic and provides connect/disconnect functions.
 *
 * @param endpoint - The SSE endpoint path (e.g., '/otc/upload-progress')
 * @param options - Callbacks for message handling, errors, and connection close
 * @returns Object with connect and disconnect functions
 */
export const useSSEConnection = <T>(
  endpoint: string,
  options: UseSSEConnectionOptions<T>
) => {
  const abortControllerRef = useRef<AbortController | null>(null);

  const disconnect = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    // Clean up any existing connection first
    disconnect();

    try {
      abortControllerRef.current = Actions.createSSEConnection<T>(endpoint, {
        onMessage: options.onMessage,
        onError: error => {
          abortControllerRef.current = null;
          options.onError?.(error);
        },
        onClose: () => {
          abortControllerRef.current = null;
          options.onClose?.();
        },
      });
    } catch (error) {
      console.error('Failed to create SSE connection:', error);
      abortControllerRef.current = null;
    }
  }, [
    endpoint,
    options.onMessage,
    options.onError,
    options.onClose,
    disconnect,
  ]);

  return { connect, disconnect };
};
